import {
  insertPositionToIndex,
  type InsertToPosition,
} from '@blocksuite/affine-shared/utils';
import { computed, type ReadonlySignal } from '@preact/signals-core';

import type { GroupBy } from '../../core/common/types.js';
import { FilterTrait, filterTraitKey } from '../../core/filter/trait.js';
import type { FilterGroup } from '../../core/filter/types.js';
import {
  emptyFilterGroup,
  normalizeFilterGroup,
} from '../../core/filter/utils.js';
import {
  GroupTrait,
  groupTraitKey,
  sortByManually,
} from '../../core/group-by/trait.js';
import { SortManager, sortTraitKey } from '../../core/sort/manager.js';
import type { Sort } from '../../core/sort/types.js';
import { PropertyBase } from '../../core/view-manager/property.js';
import { SingleViewBase } from '../../core/view-manager/single-view.js';
import {
  type TemporalProfileRole,
  temporalProfileRoleAcceptsPropertyType,
} from '../temporal-profile.js';
import { registerDefaultExtensions } from './defaults/index.js';
import type {
  GanttBarDisplayItem,
  GanttBarDisplayKind,
  GanttSnapMode,
  GanttViewData,
  GanttZoomLevel,
} from './define.js';
import {
  type BarDecorator,
  type DragConstraint,
  GanttExtensionRegistry,
  type PositionTransform,
  type SettingsSection,
} from './extensions/index.js';

/**
 * Gantt-level role a column can fill. Surfaces in `missingColumnRoles$`
 * when the user deletes the underlying column via the table view so the
 * header banner can name which role needs reconfiguring.
 */
export type GanttColumnRole =
  | 'start'
  | 'end'
  | 'progress'
  | 'status'
  | 'assignee'
  | 'dependencies';

export interface GanttExtensionRegistries {
  readonly positionTransforms: GanttExtensionRegistry<PositionTransform>;
  readonly barDecorators: GanttExtensionRegistry<BarDecorator>;
  readonly dragConstraints: GanttExtensionRegistry<DragConstraint>;
  readonly settingsSections: GanttExtensionRegistry<SettingsSection>;
}

export class GanttSingleView extends SingleViewBase<GanttViewData> {
  readonly extensions: GanttExtensionRegistries = {
    positionTransforms: new GanttExtensionRegistry<PositionTransform>(),
    barDecorators: new GanttExtensionRegistry<BarDecorator>(),
    dragConstraints: new GanttExtensionRegistry<DragConstraint>(),
    settingsSections: new GanttExtensionRegistry<SettingsSection>(),
  };

  propertiesRaw$: ReadonlySignal<GanttColumn[]> = computed(() => {
    const needShow = new Set(this.getBoundedPropertyIdsForView());
    const result: string[] = [];
    this.data$.value?.columns.forEach(v => {
      if (needShow.has(v.id)) {
        result.push(v.id);
        needShow.delete(v.id);
      }
    });
    result.push(...needShow);
    return result.map(id => this.propertyGetOrCreate(id));
  });

  properties$: ReadonlySignal<GanttColumn[]> = computed(() => {
    return this.propertiesRaw$.value.filter(p => !p.hide$.value);
  });

  detailProperties$: ReadonlySignal<GanttColumn[]> = computed(() => {
    return this.propertiesRaw$.value.filter(p => p.type$.value !== 'title');
  });

  filter$ = computed(() => normalizeFilterGroup(this.data$.value?.filter));

  filterTrait = this.traitSet(
    filterTraitKey,
    new FilterTrait(this.filter$, this, {
      filterSet: filter => {
        this.dataUpdate(() => ({ filter }));
      },
    })
  );

  private readonly sortList$ = computed(() => this.data$.value?.sort);

  private readonly sortManager = this.traitSet(
    sortTraitKey,
    new SortManager(this.sortList$, this, {
      setSortList: sortList => {
        this.dataUpdate(data => ({
          sort: {
            ...data.sort,
            ...sortList,
          },
        }));
      },
    })
  );

  groupBy$ = computed(() => this.data$.value?.groupBy);

  protected override getProjectionFilter(): FilterGroup | undefined {
    return this.filter$.value;
  }

  protected override getProjectionGroupBy(): GroupBy | undefined {
    return this.groupBy$.value;
  }

  protected override getProjectionSort(): Sort | undefined {
    return this.sortList$.value;
  }

  groupTrait = this.traitSet(
    groupTraitKey,
    new GroupTrait(this.groupBy$, this, {
      groupBySet: groupBy => {
        this.dataUpdate(() => ({ groupBy }));
      },
      sortGroup: (ids, asc) => {
        const sorted = sortByManually(
          ids,
          v => v,
          this.data$.value?.groupProperties?.map(v => v.key) ?? []
        );
        return asc === false ? sorted.reverse() : sorted;
      },
      sortRow: (key, rows) => {
        const property = this.data$.value?.groupProperties?.find(
          v => v.key === key
        );
        return sortByManually(
          rows,
          v => v.rowId,
          property?.manuallyCardSort ?? []
        );
      },
      changeGroupSort: keys => {
        const map = new Map(
          this.data$.value?.groupProperties?.map(v => [v.key, v])
        );
        this.dataUpdate(() => ({
          groupProperties: keys.map(key => {
            const property = map.get(key);
            return (
              property ?? {
                key,
                hide: false,
                manuallyCardSort: [],
              }
            );
          }),
        }));
      },
      changeRowSort: (groupKeys, groupKey, keys) => {
        const map = new Map(
          this.data$.value?.groupProperties?.map(v => [v.key, v])
        );
        this.dataUpdate(() => ({
          groupProperties: groupKeys.map(key => {
            if (key === groupKey) {
              const group = map.get(key);
              return group
                ? { ...group, manuallyCardSort: keys }
                : { key, hide: false, manuallyCardSort: keys };
            }
            return (
              map.get(key) ?? {
                key,
                hide: false,
                manuallyCardSort: [],
              }
            );
          }),
        }));
      },
      changeGroupHide: (key, hide) => {
        this.dataUpdate(() => {
          const list = [...(this.data$.value?.groupProperties ?? [])];
          const idx = list.findIndex(g => g.key === key);
          if (idx >= 0) {
            const target = list[idx];
            if (!target) return { groupProperties: list };
            list[idx] = { ...target, hide };
          } else {
            list.push({ key, hide, manuallyCardSort: [] });
          }
          return { groupProperties: list };
        });
      },
    })
  );

  mainProperties$ = computed(() => {
    const saved = this.data$.value?.header;
    return {
      titleColumn:
        saved?.titleColumn ??
        this.getBoundedRawPropertiesWindow().properties.find(
          p => p.type$.value === 'title'
        )?.id,
      iconColumn: saved?.iconColumn ?? 'type',
    };
  });

  readonly$ = computed(() => this.manager.readonly$.value);

  startDateCol$ = computed(() => this.data$.value?.startDateCol);

  endDateCol$ = computed(() => this.data$.value?.endDateCol);

  progressCol$ = computed(() => this.data$.value?.progressCol);

  statusCol$ = computed(() => this.data$.value?.statusCol);

  assigneeCol$ = computed(() => this.data$.value?.assigneeCol);

  dependenciesCol$ = computed(() => this.data$.value?.dependenciesCol);

  allDayCol$ = computed(() => this.data$.value?.allDayCol);
  timezoneCol$ = computed(() => this.data$.value?.timezoneCol);
  recurrenceCol$ = computed(() => this.data$.value?.recurrenceCol);
  recurrenceExdateCol$ = computed(() => this.data$.value?.recurrenceExdateCol);
  recurrenceIdCol$ = computed(() => this.data$.value?.recurrenceIdCol);
  calendarIdCol$ = computed(() => this.data$.value?.calendarIdCol);
  locationPhysicalCol$ = computed(() => this.data$.value?.locationPhysicalCol);
  locationVirtualCol$ = computed(() => this.data$.value?.locationVirtualCol);
  descriptionCol$ = computed(() => this.data$.value?.descriptionCol);
  attendeesCol$ = computed(() => this.data$.value?.attendeesCol);
  reminderCol$ = computed(() => this.data$.value?.reminderCol);
  visibilityCol$ = computed(() => this.data$.value?.visibilityCol);

  zoomLevel$ = computed<GanttZoomLevel>(
    () => this.data$.value?.zoomLevel ?? 'week'
  );

  snapMode$ = computed<GanttSnapMode>(
    () => this.data$.value?.snapMode ?? 'day'
  );

  /**
   * Stored title-column width (px), or `undefined` when the user has
   * never dragged it — UI falls back to its default.
   */
  labelColumnWidth$ = computed<number | undefined>(
    () => this.data$.value?.labelColumnWidth
  );

  /**
   * Stored section max-width clamp (px). `undefined` ⇒ no clamp, the
   * label section grows with its natural content width.
   */
  labelSectionMaxWidth$ = computed<number | undefined>(
    () => this.data$.value?.labelSectionMaxWidth
  );

  /**
   * Resolved bar display items. Seeds a sensible default (title only,
   * every other role hidden) when the view has no user-picked order
   * yet so the renderer never branches on "empty". Items whose role
   * column is unset on the view are tolerated — the renderer simply
   * skips them at display time, which keeps the stored order stable
   * while the user is still filling in roles via the wizard.
   */
  barDisplayItems$ = computed<readonly GanttBarDisplayItem[]>(() => {
    const stored = this.data$.value?.barDisplayItems;
    if (stored && stored.length > 0) return stored;
    return DEFAULT_BAR_DISPLAY_ITEMS;
  });

  /**
   * True once both date columns are configured. Empty-state UI hooks on this.
   */
  isConfigured$ = computed(() => {
    return Boolean(this.startDateCol$.value && this.endDateCol$.value);
  });

  /**
   * Stale column refs — a property id saved on the view data (start,
   * end, progress, status, assignee, dependencies) whose underlying
   * column has been deleted in the database. The banner in the gantt
   * header subscribes here so users get a nudge back to the wizard
   * instead of staring at silently-empty bars after someone removed a
   * column in the table view.
   *
   * Role columns are not render-window columns. A configured start/end/status
   * column may legitimately sit outside the current 50-column UI window, so
   * validation must read the full source property id list instead of the
   * current 50-column render window.
   */
  missingColumnRoles$ = computed<readonly GanttColumnRole[]>(() => {
    const existing = new Set(this.dataSource.getAllPropertyIds());
    const pairs: ReadonlyArray<[GanttColumnRole, string | undefined]> = [
      ['start', this.startDateCol$.value],
      ['end', this.endDateCol$.value],
      ['progress', this.progressCol$.value],
      ['status', this.statusCol$.value],
      ['assignee', this.assigneeCol$.value],
      ['dependencies', this.dependenciesCol$.value],
    ];
    return pairs
      .filter(([, id]) => id !== undefined && !existing.has(id))
      .map(([role]) => role);
  });

  isRoleColumn(propertyId: string): boolean {
    return (
      this.startDateCol$.value === propertyId ||
      this.endDateCol$.value === propertyId ||
      this.progressCol$.value === propertyId ||
      this.statusCol$.value === propertyId ||
      this.assigneeCol$.value === propertyId ||
      this.dependenciesCol$.value === propertyId
    );
  }

  get type(): string {
    return this.data$.value?.mode ?? 'gantt';
  }

  get view() {
    return this.data$.value;
  }

  get filter(): FilterGroup {
    return this.view?.filter ?? emptyFilterGroup;
  }

  constructor(...args: ConstructorParameters<typeof SingleViewBase>) {
    super(...args);
    registerDefaultExtensions(this.extensions);
  }

  isShow(rowId: string): boolean {
    return this.isRowVisibleByFilter(rowId, this.filter$.value);
  }

  protected override rowsMapping(
    rows: Parameters<SingleViewBase['rowsMapping']>[0]
  ): ReturnType<SingleViewBase['rowsMapping']> {
    return this.sortManager.sort(super.rowsMapping(rows));
  }

  propertyGetOrCreate(propertyId: string): GanttColumn {
    return new GanttColumn(this, propertyId);
  }

  setStartDateCol(propertyId: string | undefined): void {
    if (propertyId && !this.isTemporalRoleColumnUsable('start', propertyId)) {
      return;
    }
    this.dataUpdate(() => ({ startDateCol: propertyId }));
    if (propertyId) this.ensureDateTimeProperty(propertyId);
  }

  setEndDateCol(propertyId: string | undefined): void {
    if (propertyId && !this.isTemporalRoleColumnUsable('end', propertyId)) {
      return;
    }
    this.dataUpdate(() => ({ endDateCol: propertyId }));
    if (propertyId) this.ensureDateTimeProperty(propertyId);
  }

  setProgressCol(propertyId: string | undefined): void {
    if (
      propertyId &&
      !this.isTemporalRoleColumnUsable('progress', propertyId)
    ) {
      return;
    }
    this.dataUpdate(() => ({ progressCol: propertyId }));
  }

  setStatusCol(propertyId: string | undefined): void {
    if (propertyId && !this.isTemporalRoleColumnUsable('status', propertyId)) {
      return;
    }
    this.dataUpdate(() => ({ statusCol: propertyId }));
  }

  setAssigneeCol(propertyId: string | undefined): void {
    if (
      propertyId &&
      !this.isTemporalRoleColumnUsable('assignee', propertyId)
    ) {
      return;
    }
    this.dataUpdate(() => ({ assigneeCol: propertyId }));
  }

  setDependenciesCol(propertyId: string | undefined): void {
    if (
      propertyId &&
      !this.isTemporalRoleColumnUsable('dependencies', propertyId)
    ) {
      return;
    }
    this.dataUpdate(() => ({ dependenciesCol: propertyId }));
  }

  setZoomLevel(level: GanttZoomLevel): void {
    this.dataUpdate(() => ({ zoomLevel: level }));
  }

  setSnapMode(mode: GanttSnapMode): void {
    this.dataUpdate(() => ({ snapMode: mode }));
  }

  setLabelColumnWidth(width: number): void {
    this.dataUpdate(() => ({ labelColumnWidth: width }));
  }

  setLabelSectionMaxWidth(width: number | undefined): void {
    this.dataUpdate(() => ({ labelSectionMaxWidth: width }));
  }

  setBarDisplayItems(items: readonly GanttBarDisplayItem[]): void {
    this.dataUpdate(() => ({ barDisplayItems: [...items] }));
  }

  /**
   * Resolve a bar-display item kind to the configured column id on the
   * view. Returns `undefined` when the role hasn't been picked yet —
   * renderers must tolerate this and simply skip the chip rather than
   * throwing, because the user may be mid-configuration.
   */
  resolveBarItemColumn(kind: GanttBarDisplayKind): string | undefined {
    switch (kind) {
      case 'title':
        return this.mainProperties$.value.titleColumn;
      case 'assignee':
        return this.assigneeCol$.value;
      case 'start':
        return this.startDateCol$.value;
      case 'end':
        return this.endDateCol$.value;
      case 'status':
        return this.statusCol$.value;
      case 'progress':
        return this.progressCol$.value;
    }
  }

  private ensureDateTimeProperty(propertyId: string): void {
    if (this.dataSource.propertyTypeGet(propertyId) !== 'date') return;
    const property = this.propertyGetOrCreate(propertyId);
    property.dataUpdate(data => ({
      ...data,
      includeTime: true,
    }));
  }

  private isTemporalRoleColumnUsable(
    role: TemporalProfileRole,
    propertyId: string | undefined
  ): boolean {
    if (!propertyId) return false;
    return temporalProfileRoleAcceptsPropertyType(
      role,
      this.dataSource.propertyTypeGet(propertyId),
      this.dataSource.propertyDataGet(propertyId),
      this.selfBlockId
    );
  }

  private get selfBlockId(): string {
    return (
      (this.manager.dataSource as unknown as { blockId?: string }).blockId ?? ''
    );
  }
}

// Default order shown on bars before the user customises anything.
// Follows the "bar = identity layer" convention used by Linear, Asana,
// Monday and Jira Timeline: bar carries just enough to recognise the
// task (title + owner glyph), everything else reads through visual
// encoding — status drives the bar's background colour (see
// `resolveBarStatusColor` in bar-renderer), progress drives the
// left-to-right fill overlay. When no status column is wired or the
// picked option has no colour, the bar falls back to the theme
// primary via barStyle's `&&&[data-classification]` rule. Dates stay
// off-bar by default because the time-axis above the bar IS the date
// view; showing them again as chips wastes horizontal space on short
// bars. Users can still turn any item back on via the bar-display
// popover.
export const DEFAULT_BAR_DISPLAY_ITEMS: readonly GanttBarDisplayItem[] = [
  { kind: 'title', visible: true },
  { kind: 'assignee', visible: true, variant: 'avatar' },
  { kind: 'status', visible: false },
  { kind: 'progress', visible: false },
  { kind: 'start', visible: false },
  { kind: 'end', visible: false },
];

type GanttColumnData = GanttViewData['columns'][number];

export class GanttColumn extends PropertyBase {
  override move(position: InsertToPosition): void {
    this.ganttView.dataUpdate(view => {
      const columnIndex = view.columns.findIndex(v => v.id === this.id);
      const columns = [...view.columns];
      const [column] =
        columnIndex >= 0
          ? columns.splice(columnIndex, 1)
          : [{ id: this.id, hide: this.hide$.value }];
      if (!column) return {};

      if (typeof position === 'object') {
        const targetIndex = columns.findIndex(v => v.id === position.id);
        if (targetIndex < 0) {
          const target = this.ganttView.propertyGetOrCreate(position.id);
          columns.push({ id: position.id, hide: target.hide$.value });
        }
      }

      const index = insertPositionToIndex(position, columns);
      columns.splice(index, 0, column);
      return { columns };
    });
  }

  override hideSet(hide: boolean): void {
    this.viewDataUpdate(data => ({ ...data, hide }));
  }

  hide$ = computed(() => {
    if (this.type$.value === 'title') return false;
    const hideFromViewData = this.viewData$.value?.hide;
    if (hideFromViewData != null) return hideFromViewData;
    return !this.ganttView.isRoleColumn(this.id);
  });

  width$ = computed<number | undefined>(() => this.viewData$.value?.width);

  widthSet(width: number | undefined): void {
    this.viewDataUpdate(data => ({ ...data, width }));
  }

  viewData$ = computed(() => {
    return this.ganttView.data$.value?.columns.find(v => v.id === this.id);
  });

  viewDataUpdate(
    updater: (viewData: GanttColumnData) => Partial<GanttColumnData>
  ): void {
    this.ganttView.dataUpdate(data => {
      const existing = data.columns.find(v => v.id === this.id);
      if (existing) {
        return {
          ...data,
          columns: data.columns.map(v =>
            v.id === this.id ? { ...v, ...updater(v) } : v
          ),
        };
      }
      const newEntry: GanttColumnData = {
        id: this.id,
        hide: false,
        ...updater({ id: this.id, hide: false }),
      };
      return {
        ...data,
        columns: [...data.columns, newEntry],
      };
    });
  }

  constructor(
    private readonly ganttView: GanttSingleView,
    columnId: string
  ) {
    super(ganttView, columnId);
  }
}
